import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { OpsUserService } from '@service/ops-user.service';
import { UserAndPasswordVisible } from '@src/app/pages/ops-settings/ops-account/ops-account.component';
import { ChannelCategory } from 'icreate/common/models/channel-category';
import { OpsUserCategories } from 'icreate/common/models/ops-user-category';
import { Vessel } from 'icreate/common/models/vessel';
import { getValidationErrors, ValidationErrorMap } from 'icreate/common/validators';
import { MappedErrors } from 'icreate/common/validators/custom-functions';
import { opsUserValidator } from 'icreate/common/validators/ops-user/ops-user';
import _ from 'lodash';

enum AccountInputName {
    ACCOUNT_NAME = 'account-name',
    PASSWORD = 'password',
    CONFIRM_PASSWORD = 'confirm-password',
    CATEGORY = 'category',
}

@Component({
    selector: 'app-new-account-dialog',
    templateUrl: './new-account-dialog.component.html',
    styleUrls: ['./new-account-dialog.component.css'],
    standalone: false,
})
export class NewAccountDialogComponent implements OnInit {
    readonly ACCOUNT_NAME_INPUT = AccountInputName.ACCOUNT_NAME;
    readonly PASSWORD_INPUT = AccountInputName.PASSWORD;
    readonly CONFIRM_PASSWORD_INPUT = AccountInputName.CONFIRM_PASSWORD;

    name: string = '';
    password: string = '';
    confirmPasssword: string = '';
    opsUserId: string = '';

    validationErrors: ValidationErrorMap = {};

    checkMinimum: boolean = false;
    checkLeastLetterAndNumber: boolean = false;
    checkLeastSymbols: boolean = false;
    isEditing: boolean = false;
    isDuplicate: boolean = false;

    hasErrors: boolean = false;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            vessel: Vessel;
            selectedCategoryList: ChannelCategory[];
            id: string;
            name: string;
            password: string;
            controlMimic: boolean;
            isAdmin: boolean;
            isDuplicate: boolean;
            permissions: OpsUserCategories[];
            userList: UserAndPasswordVisible[];
        },
        private dialogRef: MatDialogRef<NewAccountDialogComponent>,
        private opsUserService: OpsUserService,
        private translate: TranslateService
    ) {}

    ngOnInit() {
        this.isDuplicate = this.data.isDuplicate ?? false;
        this.opsUserId = this.data.id;
        this.name = this.data.name ?? '';
        this.password = this.data.password ?? '';
        this.confirmPasssword = this.data.password ?? '';
        this.isEditing = !!this.opsUserId;

        this.updateDialogState();
    }

    cancelClick(result: boolean = false) {
        this.dialogRef.close(result);
    }

    onInputChange(event: { name: string; value: string }) {
        if (event.name === this.ACCOUNT_NAME_INPUT) {
            this.name = event.value;
        } else if (event.name === this.PASSWORD_INPUT) {
            this.password = event.value;
        } else if (event.name === this.CONFIRM_PASSWORD_INPUT) {
            this.confirmPasssword = event.value;
        }

        this.updateDialogState();
    }

    generatePassword() {
        const maxLength = 16;
        const minLength = 8;
        const length = Math.floor(Math.random() * (maxLength - minLength + 1)) + minLength;
        const letters = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const digits = '0123456789';
        const symbols = '!\"#$%&\'()';

        const requiredChars = [
            letters[Math.floor(Math.random() * letters.length)],
            digits[Math.floor(Math.random() * digits.length)],
            symbols[Math.floor(Math.random() * symbols.length)],
        ];

        const allChars = letters + digits + symbols;

        const remainingLength = length - requiredChars.length;
        const passwordChars: string[] = [...requiredChars];

        for (let i = 0; i < remainingLength; i++) {
            passwordChars.push(allChars[Math.floor(Math.random() * allChars.length)]);
        }

        const newPassword = this.shuffleArray(passwordChars).join('');

        this.password = newPassword;
        this.confirmPasssword = newPassword;

        this.updateDialogState();
    }

    shuffleArray(array) {
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
    }

    computeDialogErrors(): MappedErrors {
        const accountData = {
            name: this.name,
            password: this.password,
        };

        const errors = getValidationErrors(opsUserValidator(), accountData) || [];
        const mappedErrors: MappedErrors = {};

        const passwordErrors = errors.filter(e => e.path === 'password');

        this.checkMinimum = !passwordErrors.some(e => e.message === 'errors.validation.generic.below-minimum');

        this.checkLeastLetterAndNumber = !passwordErrors.some(
            e =>
                e.message === 'account-settings.password.error.letter-required' ||
                e.message === 'account-settings.password.error.number-required'
        );

        this.checkLeastSymbols = !passwordErrors.some(
            e => e.message === 'account-settings.password.error.special-char-required'
        );

        const isNameRepeated = !!this.data.userList.find(user => user.name == this.name && user.id != this.opsUserId);

        if (isNameRepeated) {
            mappedErrors['name'] = {
                message: this.translate.instant('account-settings.input.name.error.duplicate'),
            } as any;
        }

        for (const error of errors) {
            if (error.message === 'errors.validation.generic.below-minimum') {
                error.message = this.translate.instant('errors.validation.generic.below-minimum', {
                    min: 8,
                });
            }

            if (!mappedErrors[error.path]) {
                mappedErrors[error.path] = error;
            } else {
                mappedErrors[error.path].errors = [...(mappedErrors[error.path].errors || []), error.message];
            }
        }

        if (this.password !== this.confirmPasssword) {
            mappedErrors.confirmPasssword = {
                message: 'account-settings.password.error.not-match',
            } as any;
        } else if (mappedErrors.password) {
            mappedErrors.confirmPasssword = {
                message: mappedErrors.password.message,
            } as any;
        }

        return mappedErrors;
    }

    updateDialogState(): void {
        const mappedErrors = this.computeDialogErrors();
        this.validationErrors = Object.keys(mappedErrors).length ? mappedErrors : null;
        this.hasErrors = !!this.validationErrors;
    }

    isCreatingDisabled(): boolean {
        return this.hasErrors;
    }

    async createButtonClick() {
        try {
            if (!this.isEditing) {
                await this.opsUserService.create({
                    vesselId: this.data.vessel.id,
                    name: this.name,
                    password: this.password,
                    isAdmin: false,
                    categories: this.isDuplicate
                        ? (this.data.permissions || []).map(permission => ({
                              id: permission.categoryId,
                              editChannel: permission.editChannel,
                              controlMimic: permission.controlMimic,
                          }))
                        : [],
                });
            } else {
                await this.opsUserService.update({
                    id: this.opsUserId,
                    vesselId: this.data.vessel.id,
                    name: this.name,
                    password: this.password,
                    isAdmin: this.data.isAdmin,
                });
            }
        } catch (error) {
            console.log(error);
        }

        this.cancelClick(true);
    }
}
